import { useMemo, useState } from 'react'
import { useQuery, useAction } from 'convex/react'
import { api } from '../../../../convex/_generated/api'
import { Card } from '@/components/ui/card'
import { RefreshCw, TrendingUp, TrendingDown } from 'lucide-react'
import { cn } from '@/lib/utils'
import {
  ResponsiveContainer,
  AreaChart,
  Area,
  LineChart,
  Line,
  XAxis,
  YAxis,
  Tooltip,
  CartesianGrid,
} from 'recharts'

type MrrPoint = { day: string; mrr: number | null }
type ChurnPoint = { day: string; churnRate: number }

const RANGES = [
  { key: 30, label: '30d' },
  { key: 60, label: '60d' },
  { key: 180, label: '180d' },
  { key: 365, label: '365d' },
] as const

type RangeKey = (typeof RANGES)[number]['key']

function fmtUsdCompact(v: number): string {
  if (v >= 1000) return `$${(v / 1000).toFixed(1)}k`
  return `$${v.toFixed(0)}`
}

function fmtUsdFull(v: number): string {
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
    maximumFractionDigits: 0,
  }).format(v)
}

function shortDate(iso: string): string {
  const [, m, d] = iso.split('-')
  return `${Number(m)}/${Number(d)}`
}

function midDate(iso: string): string {
  const [y, m, d] = iso.split('-')
  const dt = new Date(Number(y), Number(m) - 1, Number(d))
  return dt.toLocaleDateString('en-US', { month: 'short', day: 'numeric' })
}

function longDate(iso: string): string {
  const [y, m, d] = iso.split('-')
  const dt = new Date(Number(y), Number(m) - 1, Number(d))
  return dt.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })
}

function xTickFormatter(range: RangeKey): (iso: string) => string {
  return range >= 180 ? midDate : shortDate
}

type MrrTooltipProps = { active?: boolean; payload?: { payload?: MrrPoint }[] }
function MrrTooltip({ active, payload }: MrrTooltipProps) {
  if (!active || !payload?.length) return null
  const p = payload[0]?.payload
  if (!p) return null
  return (
    <div className="rounded-md border border-neutral-200 bg-white px-3 py-2 shadow-sm text-xs min-w-[140px]">
      <div className="font-medium text-neutral-900 mb-1.5">{longDate(p.day)}</div>
      <div className="flex items-center gap-2">
        <span className="inline-block h-2 w-2 rounded-full" style={{ background: '#10B981' }} />
        <span className="text-neutral-600">MRR</span>
        <span className="ml-auto font-medium text-emerald-600">
          {p.mrr == null ? '—' : fmtUsdFull(p.mrr)}
        </span>
      </div>
    </div>
  )
}

type ChurnTooltipProps = { active?: boolean; payload?: { payload?: ChurnPoint }[] }
function ChurnTooltip({ active, payload }: ChurnTooltipProps) {
  if (!active || !payload?.length) return null
  const p = payload[0]?.payload
  if (!p) return null
  return (
    <div className="rounded-md border border-neutral-200 bg-white px-3 py-2 shadow-sm text-xs min-w-[170px]">
      <div className="font-medium text-neutral-900 mb-1.5">{longDate(p.day)}</div>
      <div className="flex items-center gap-2">
        <span className="inline-block h-2 w-2 rounded-full" style={{ background: '#EF4444' }} />
        <span className="text-neutral-600">Churn rate (trailing 30d)</span>
        <span className="ml-auto font-medium text-red-600">{p.churnRate}%</span>
      </div>
    </div>
  )
}

function RangeSwitcher({
  value,
  onChange,
}: {
  value: RangeKey
  onChange: (next: RangeKey) => void
}) {
  return (
    <div className="flex items-center gap-0.5 rounded-md border border-neutral-200 bg-white p-0.5">
      {RANGES.map((r) => (
        <button
          key={r.key}
          type="button"
          onClick={() => onChange(r.key)}
          className={cn(
            'px-2 py-0.5 text-[11px] font-medium rounded transition-colors',
            value === r.key
              ? 'bg-neutral-900 text-white'
              : 'text-neutral-500 hover:text-neutral-900 hover:bg-neutral-100',
          )}
        >
          {r.label}
        </button>
      ))}
    </div>
  )
}

export function SubscriptionCharts() {
  const mrrData = useQuery(api.stripeMrr.getDaily, {})
  const churnData = useQuery(api.stripeMrr.getChurnSeries, {})
  const sync = useAction(api.stripeSigmaSync.syncDailyMetrics)
  const [syncing, setSyncing] = useState(false)
  const [mrrRange, setMrrRange] = useState<RangeKey>(30)
  const [churnRange, setChurnRange] = useState<RangeKey>(30)

  const onSync = async () => {
    setSyncing(true)
    try {
      await sync({})
    } catch (e) {
      console.error('[StripeSigma] sync failed', e)
    } finally {
      setSyncing(false)
    }
  }

  const fullMrrSeries: MrrPoint[] = mrrData?.series ?? []
  const fullChurnSeries: ChurnPoint[] = churnData?.series ?? []
  const mrrSeries = useMemo(
    () => (fullMrrSeries.length === 0 ? [] : fullMrrSeries.slice(-mrrRange)),
    [fullMrrSeries, mrrRange],
  )
  const churnSeries = useMemo(
    () => (fullChurnSeries.length === 0 ? [] : fullChurnSeries.slice(-churnRange)),
    [fullChurnSeries, churnRange],
  )

  const lastSyncedAt = Math.max(mrrData?.lastSyncedAt ?? 0, churnData?.lastSyncedAt ?? 0) || null

  const isMrrLoading = mrrData === undefined
  const isChurnLoading = churnData === undefined
  const mrrHasData = mrrSeries.length > 0
  const churnHasData = churnSeries.length > 0

  return (
    <div className="space-y-3">
      <div className="flex items-center justify-end gap-3">
        {lastSyncedAt && (
          <span className="text-[11px] text-neutral-400">
            Sigma synced {new Date(lastSyncedAt).toLocaleString()}
          </span>
        )}
        <button
          onClick={onSync}
          disabled={syncing}
          className="inline-flex items-center gap-1.5 rounded-md border border-neutral-200 bg-white px-3 py-1.5 text-xs font-medium text-neutral-700 hover:bg-neutral-50 disabled:opacity-50"
          title="Pull fresh data from Stripe Sigma"
        >
          <RefreshCw className={cn('h-3.5 w-3.5', syncing && 'animate-spin')} />
          Sync Sigma
        </button>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 sm:gap-6">
        <Card className="overflow-hidden p-0">
          <div className="px-5 pt-5 pb-1 flex items-start justify-between gap-3">
            <div className="space-y-0.5">
              <div className="flex items-center gap-2">
                <TrendingUp className="h-4 w-4 text-emerald-500" />
                <h2 className="text-base font-semibold text-neutral-900">MRR</h2>
              </div>
              <p className="text-xs text-neutral-500">Daily, via Sigma</p>
            </div>
            <RangeSwitcher value={mrrRange} onChange={setMrrRange} />
          </div>
          <div className="h-[300px] px-2 pt-3 pb-2">
            {isMrrLoading ? (
              <div className="h-full flex items-center justify-center text-sm text-neutral-400">
                Loading…
              </div>
            ) : !mrrHasData ? (
              <div className="h-full flex items-center justify-center flex-col gap-2 text-sm text-neutral-400">
                <span>No snapshots yet.</span>
                <span className="text-xs">Click Sync Sigma to pull data.</span>
              </div>
            ) : (
              <ResponsiveContainer width="100%" height="100%">
                <AreaChart data={mrrSeries} margin={{ top: 6, right: 16, left: 0, bottom: 0 }}>
                  <defs>
                    <linearGradient id="mrrGradient" x1="0" y1="0" x2="0" y2="1">
                      <stop offset="0%" stopColor="#10B981" stopOpacity={0.32} />
                      <stop offset="100%" stopColor="#10B981" stopOpacity={0} />
                    </linearGradient>
                  </defs>
                  <CartesianGrid stroke="#f1f5f9" vertical={false} />
                  <XAxis
                    dataKey="day"
                    tickFormatter={xTickFormatter(mrrRange)}
                    tick={{ fill: '#94a3b8', fontSize: 11 }}
                    axisLine={false}
                    tickLine={false}
                    minTickGap={28}
                  />
                  <YAxis
                    tickFormatter={fmtUsdCompact}
                    tick={{ fill: '#94a3b8', fontSize: 11 }}
                    axisLine={false}
                    tickLine={false}
                    width={52}
                  />
                  <Tooltip content={<MrrTooltip />} cursor={{ stroke: '#e2e8f0', strokeWidth: 1 }} />
                  <Area
                    type="linear"
                    dataKey="mrr"
                    stroke="#10B981"
                    strokeWidth={2.5}
                    fill="url(#mrrGradient)"
                    isAnimationActive={false}
                  />
                </AreaChart>
              </ResponsiveContainer>
            )}
          </div>
        </Card>

        <Card className="overflow-hidden p-0">
          <div className="px-5 pt-5 pb-1 flex items-start justify-between gap-3">
            <div className="space-y-0.5">
              <div className="flex items-center gap-2">
                <TrendingDown className="h-4 w-4 text-red-500" />
                <h2 className="text-base font-semibold text-neutral-900">Subscriber Churn</h2>
              </div>
              <p className="text-xs text-neutral-500">Trailing 30-day rate, daily — via Sigma</p>
            </div>
            <RangeSwitcher value={churnRange} onChange={setChurnRange} />
          </div>
          <div className="h-[300px] px-2 pt-3 pb-2">
            {isChurnLoading ? (
              <div className="h-full flex items-center justify-center text-sm text-neutral-400">
                Loading…
              </div>
            ) : !churnHasData ? (
              <div className="h-full flex items-center justify-center text-sm text-neutral-400">
                No data yet.
              </div>
            ) : (
              <ResponsiveContainer width="100%" height="100%">
                <LineChart data={churnSeries} margin={{ top: 6, right: 16, left: 0, bottom: 0 }}>
                  <CartesianGrid stroke="#f1f5f9" vertical={false} />
                  <XAxis
                    dataKey="day"
                    tickFormatter={xTickFormatter(churnRange)}
                    tick={{ fill: '#94a3b8', fontSize: 11 }}
                    axisLine={false}
                    tickLine={false}
                    minTickGap={28}
                  />
                  <YAxis
                    tickFormatter={(v: number) => `${v}%`}
                    tick={{ fill: '#94a3b8', fontSize: 11 }}
                    axisLine={false}
                    tickLine={false}
                    width={44}
                    domain={[0, (max: number) => Math.max(5, Math.ceil(max / 5) * 5)]}
                  />
                  <Tooltip content={<ChurnTooltip />} cursor={{ stroke: '#e2e8f0', strokeWidth: 1 }} />
                  <Line
                    type="linear"
                    dataKey="churnRate"
                    stroke="#EF4444"
                    strokeWidth={2.5}
                    dot={churnRange <= 60 ? { r: 2.5, fill: '#EF4444', strokeWidth: 0 } : false}
                    activeDot={{ r: 5 }}
                    isAnimationActive={false}
                  />
                </LineChart>
              </ResponsiveContainer>
            )}
          </div>
        </Card>
      </div>
    </div>
  )
}
